import { useCallback, useEffect } from "react";
import { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { useAtom, useSetAtom } from "jotai";
import SectionRecord from "models/view-models/section-record";
import { DraggableSection } from "utilities/atoms/section-drag-and-drop/draggable-section";
import { DroppedSection } from "utilities/atoms/section-drag-and-drop/dropped-section";
import { ActiveSectionDraggable } from "organisms/section-drag-and-drop/active-section-draggable";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { useRegisterDragAndDropScopeContext } from "utilities/contexts/shared-drag-and-drop/register-drag-and-drop-scope-context";

export const SECTION_DRAG_AND_DROP_SCOPE_NAME = "section";
export const SECTION_DROPPABLE_ID = "section-droppable";
export const SECTION_DRAGGABLE_ID = "section-draggable";

const useDragAndDropSection = () => {
    const [draggableSection, setDraggableSection] = useAtom(DraggableSection);
    const setDroppedSection = useSetAtom(DroppedSection);

    const handleDragStart = useCallback(
        (event: DragStartEvent) => {
            setDraggableSection(
                event.active.data.current?.section as SectionRecord
            );
        },
        [setDraggableSection]
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            if (event.over && event.over.id === SECTION_DROPPABLE_ID) {
                setDroppedSection(draggableSection);
                setDraggableSection(undefined);
            }
        },
        [draggableSection, setDraggableSection, setDroppedSection]
    );

    const { registerDragAndDropScope } = useRegisterDragAndDropScopeContext();

    useEffect(
        () =>
            registerDragAndDropScope(SECTION_DRAG_AND_DROP_SCOPE_NAME, {
                draggableComponent: ActiveSectionDraggable,
                handleDragEnd: handleDragEnd,
                handleDragStart: handleDragStart,
                overlayModifiers: [snapCenterToCursor],
            }),
        [handleDragEnd, handleDragStart, registerDragAndDropScope]
    );
};

export default useDragAndDropSection;
